import { useMutation } from "@apollo/client";
import { Button, message } from "antd";

import {
  AssetErrorDocument,
  AssetErrorHandleDocument,
  AssetErrorResolveDocument,
} from "../../graphql/graphql";
import { depromisify } from "../../utils/depromisify";

/**
 * A button that calls the handleAssetError mutation when clicked.
 */
const HandleAssetErrorButton = ({ assetErrorId }: { assetErrorId: string }) => {
  const [mutation, { data, error, loading }] = useMutation(
    AssetErrorHandleDocument,
  );

  const mutate = depromisify(async () => {
    await mutation({
      variables: { assetErrorId },
      refetchQueries: [AssetErrorDocument],
    });
    if (error ?? data?.assetErrorHandle === "ERROR") {
      await message.error("Failed to resolve error");
    }
  });

  return (
    <Button onClick={mutate} loading={loading} type="primary">
      Handle Error
    </Button>
  );
};

/**
 * A button that calls the resolveAssetError mutation when clicked.
 */
const ResolveAssetErrorButton = ({
  assetErrorId,
}: {
  assetErrorId: string;
}) => {
  const [mutation, { data, error, loading }] = useMutation(
    AssetErrorResolveDocument,
  );

  const mutate = depromisify(async () => {
    await mutation({
      variables: { assetErrorId },
      refetchQueries: [AssetErrorDocument],
    });
    if (error ?? data?.assetErrorResolve === "ERROR") {
      await message.error("Failed to resolve error");
    }
  });

  return (
    <Button onClick={mutate} loading={loading} type="primary">
      Resolve Error
    </Button>
  );
};

/**
 * A component that displays the appropriate actions for a asset error.
 */
const AssetErrorActions = ({
  assetError,
}: {
  assetError: {
    id: string;
    resolvedAt?: string | null;
    handledAt?: string | null;
  };
}) => {
  if (
    assetError.resolvedAt === undefined ||
    assetError.handledAt === undefined
  ) {
    return <></>;
  }

  if (assetError.handledAt === null) {
    // Show the handle error button if the error has not been handled yet
    return <HandleAssetErrorButton assetErrorId={assetError.id} />;
  } else if (assetError.resolvedAt === null) {
    // Show the resolve error button if the error has been handled but not resolved yet
    return <ResolveAssetErrorButton assetErrorId={assetError.id} />;
  } else {
    return <></>;
  }
};

export default AssetErrorActions;
